import React, { FC, useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { AppContext } from "Context/AppContext";
import Label from "Components/Common/components/Label";
import { Link } from "react-router-dom";
import { EDIT, ITEMS, LISTS, LISTS_ROUTE, LIST_NAME, LIST_READ, ListsEditHeaders, TAXONOMY, } from "./constants";
import { AddIconWithBorder, DragDropIcon, ImageIcon, InfoIcon, LeftVectorIcon } from "assests/GmsIcons";
import { DragDropContext, Droppable, Draggable, DropResult } from "react-beautiful-dnd";
import ToggleButton from "Components/Common/components/Inputs/ToggleButton";
import useProductAttributeImport from "../ISR/Pages/DefineModel/Hooks/useProductAttributeImport";
import TableSkeletonElement from "Components/GMS/Products/components/TableSkeletonElement";
import { toast } from "react-toastify";
import OverlayIcon from "Components/GMS/Rules/Components/OverlayIcon";
import useTable from "Components/Common/Table/useTable";
import { HelpTextData } from "../ISR/Pages/DefineModel/model";
import { getListItem } from "./Services/ListServices";

interface Row {
    title: string;
    display_value: string;
    visibility: boolean;
    image: string;
    id?: string;
    math_value: number | null
    helpText?: HelpTextData;
}

interface ListObj {
    id?: string;
    name: string;
    industry_id: string;
    items: Row[];
    imported_attribute: string;
    created_at: string;
    created_by: string;
    updated_at?: string;
    updated_by?: string;
}

const ListsDetails: FC = (props: any) => {
    let appContext = useContext(AppContext);
    const { params } = props.match;
    const { id } = params;
    const [loading, setLoading] = useState(false);
    const appData = useContext(AppContext);
    const history = useHistory();
    const [tableData, setTableData] = useState<Row[]>();
    const [models, setModels] = useState<string[]>([]);
    const [listName, setListName] = useState<string>();
    const [editState, setEditState] = useState<ListObj>();
    const { fieldDataConfiguration } =
        useProductAttributeImport({ attributeId: editState?.imported_attribute as string });

    const { headers, sort, handleSortByColumn } =
        useTable({ table_headers: ListsEditHeaders });

    const [lastTableRowRef, setLastTableRowRef] = useState<any>();
    const readPermission = appContext.pm.checkPermission(LIST_READ);

    useEffect(() => {
        if (lastTableRowRef) {
            lastTableRowRef?.scrollIntoView({ behavior: 'smooth', block: 'end' })
        }
    }, [lastTableRowRef]);


    useEffect(() => {
        if (!readPermission) {
            history.push("/workspace/module/permission/denied");
        }
    }, [appData.channelKey]);

    useEffect(() => {
        const table: Row[] = fieldDataConfiguration.choices?.map((choice) => ({
            title: choice.av_value,
            display_value: choice.av_value,
            visibility: true,
            image: "",
            math_value: null
        })) || [];
        setTableData((prev: Row[] | undefined) => {
            if (!prev) return table;
            const newTable = table.filter(x => {
                if (!prev?.find(y => y.title === x.title)) return x
            })
            return [...prev, ...newTable];
        });
    }, [fieldDataConfiguration]);

    useEffect(() => {
        const fetchEditState = async () => {
            try {
                setLoading(true);
                const res = await getListItem(id);
                if (res?.data?.done) {
                    setListName(res?.data?.data?.name);
                    setTableData(res?.data?.data?.items);
                    setModels(res?.data?.data?.models);
                    setEditState(res?.data?.data);
                }
            } catch (error) {
                console.log(error);
            } finally {
                setLoading(false);
            }
        };
        if (
            ![null, "null", "undefined", undefined].includes(id) &&
            !editState
        ) {
            fetchEditState();
        }
    }, []);

    const onDragEnd = (result: DropResult) => {
        if (!result.destination) return;
        const items = Array.from(tableData ?? []);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setTableData(items);
    };

    const onInputChangeHandler = (
        event: React.ChangeEvent<HTMLInputElement>,
        index: number,
        type: string
    ) => {
        event.persist();
        const val = event?.target?.value;
        setTableData((prev) => {
            const newValue = prev?.map((entry, id) => {
                if (id === index) {
                    switch (type) {
                        case "title":
                            entry.title = val;
                            break;
                        case "display_value":
                            entry.display_value = val;
                            break;
                        case "math_value":
                            if (isNaN(Number(val))) {
                                toast.info("Math value must be an integer")
                                break
                            }
                            entry.math_value = Number(val);
                            break;
                        default:
                            break;
                    }
                }
                return entry;
            });
            return newValue;
        });
    };

    const onVisibilityChangeHandler = (
        e: React.ChangeEvent<HTMLInputElement>,
        index: number
    ) => {
        setTableData((prev) => {
            const newValue = prev?.map((entry, id) => {
                if (id === index) entry.visibility = !entry?.visibility;
                return entry;
            });
            return newValue;
        });
    };

    return (
        <React.Fragment>
            <div className="tw-mt-1">
                <nav className="tw-flex" aria-label="Breadcrumb">
                    <ol className="tw-inline-flex tw-items-center tw-space-x-1 tw-md:space-x-2 tw-rtl:space-x-reverse">
                        <li className="tw-inline-flex tw-items-center tw-pointer-events-none">
                            <Label
                                message={TAXONOMY}
                                fontSize={"0.75"}
                                fontWeight="400"
                                color="#5c5f62"
                            />
                        </li>
                        <li>/</li>
                        <li className="tw-inline-flex tw-items-center">
                            <Link to={LISTS_ROUTE}>
                                <Label
                                    message={LISTS}
                                    fontSize={"0.75"}
                                    fontWeight="500"
                                    color="#5c5f62"
                                />
                            </Link>
                        </li>
                        <li>/</li>
                        <li className="tw-inline-flex tw-items-center tw-pointer-events-none" aria-current="page">
                            <Label
                                message={listName}
                                fontSize={"0.75"}
                                fontWeight="500"
                                color="#5c5f62"
                            />
                        </li>
                    </ol>
                </nav>
            </div>
            <div className={`tw-px-[21px] tw-pt-[12px] tw-flex tw-justify-between`}>
                <Link
                    to={"/gms/taxonomy/lists"}
                    className="tw-float-left tw-w-[4%] tw-mt-1.5"
                >
                    <LeftVectorIcon />
                </Link>
                <div
                    className={`tw-flex tw-flex-col tw-items-left tw-text-xs tw-w-[96%]`}
                >
                    <label
                        className={`tw-float-right tw-text-black tw-font-noto-sans tw-text-xl tw-font-semibold tw-leading-[25.2px]`}
                    >
                        {LISTS}
                    </label>
                </div>
                <div className={`tw-flex`}>
                    <div>
                        <button
                            onClick={() => {
                                if (id) {
                                    history.replace({ pathname: `/gms/taxonomy/lists/edit/${id}` });
                                }
                            }}
                            className="tw-bg-white tw-text-blue-800 tw-border tw-border-blue-800 tw-hover:bg-blue-400 tw-font-bold tw-py-2 tw-px-4 tw-ml-2 tw-mr-2 tw-rounded tw-items-center"
                        >
                            {EDIT}
                        </button>
                    </div>
                </div>
            </div>
            <div
                className={`tw-ml-5 tw-mt-2 tw-box-border tw-w-auto tw-border-t-[1px] tw-border-solid tw-border-lightgray-200`}
            />
            {loading ? (
                <TableSkeletonElement />
            ) : (
                <>
                    <div className="tw-flex tw-mt-2 tw-mb-2">
                        <div className="tw-w-[30%]">
                            <label className="tw-text-black tw-text-sm tw-ml-4 tw-mb-2">
                                {LIST_NAME}
                                <span className="tw-text-red-500 tw-ml-2">*</span>
                            </label>
                            <input
                                placeholder="Enter Name"
                                className=" tw-placeholder-gray-200 tw-ml-4 tw-mb-4 tw-w-[364px] tw-h-9 tw-bg-gray-100 tw-rounded tw-border tw-border-zinc-300 tw-p-2"
                                value={listName}
                                onChange={(e) => {
                                    setListName(e?.target?.value);
                                }}
                                disabled={true}
                            />
                        </div>
                        <div className="tw-w-[70%] tw-ml-16">
                            <label className="tw-text-black tw-text-sm tw-ml-4 tw-mb-1 tw-w-full">
                                Associated Models
                            </label>
                            {models?.length > 0 ? models?.map((model, index) =>
                                <div key={index} className="tw-px-[8px] tw-bg-[#f4f5f7] tw-m-1 tw-rounded-sm tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex">
                                    <div className="tw-text-[#172b4d]  tw-text-xs tw-w-auto">{model}</div>
                                </div>) :
                                <div className="tw-text-[#172b4d] tw-ml-4 tw-text-xs">
                                    No Models Associated
                                </div>}
                        </div>
                    </div>
                    <div
                        ref={(el) => {
                            setLastTableRowRef(el)
                        }}
                        className="tw-ml-4 tw-w-[97%] tw-h-[50%] tw-bg-white tw-rounded tw-border tw-border-gray-200">
                        <div className="tw-flex tw-justify-between">
                            <div className="tw-text-slate-500 tw-text-s tw-font-bold tw-font-['Noto Sans'] tw-leading-tight tw-mt-4 tw-mb-4 tw-ml-2">
                                {ITEMS}
                            </div>
                        </div>
                        <DragDropContext onDragEnd={onDragEnd} >
                            <Droppable droppableId="rows">
                                {(provided) => (
                                    <div className="tw-max-h-[510px] tw-overflow-x-auto ">
                                        <table
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            className=" tw-table-auto tw-w-full tw-overflow-scroll"
                                        >
                                            <thead className=" tw-sticky tw-top-0 tw-bg-zinc-200  tw-border-zinc-200 tw-h-12 tw-font-noto-sans  tw-mt-[42px] tw-mb-[12px] tw-border tw-z-40">
                                                <tr >
                                                    {headers.map(
                                                        (
                                                            {
                                                                label,
                                                                ...rest
                                                            }: {
                                                                label: string;
                                                                options: any;
                                                                map: string;
                                                            },
                                                            index: number
                                                        ) => {
                                                            return (
                                                                <th
                                                                    key={`${label}_${index}`}
                                                                    className=" tw-sticky tw-text-slate-500 tw-text-sm tw-font-semibold tw-font-['Noto Sans']"

                                                                    scope="col"
                                                                >
                                                                    <div
                                                                        className="tw-flex tw-align-middle"
                                                                        onClick={() => {
                                                                            rest.options?.sort &&
                                                                                handleSortByColumn(rest.map);
                                                                        }}
                                                                    >
                                                                        <label className="tw-text-[#5c5f62]">
                                                                            {label}
                                                                            {rest.options?.infoMessage && (
                                                                                <span className="tw-ml-1 tw-inline-block">
                                                                                    <OverlayIcon
                                                                                        message={rest.options?.infoMessage ?? "Display value will show on the runtime in any application. If its empty it shows the default title. "}
                                                                                        placement="bottom"
                                                                                    />
                                                                                </span>
                                                                            )}
                                                                        </label>
                                                                        &nbsp;
                                                                        <span className="tw-ms-2">
                                                                            {rest.options?.sort &&
                                                                                (sort.column === rest.map ? (
                                                                                    <i
                                                                                        className={`fas fa-sort-${sort.direction === "asc"
                                                                                            ? "up"
                                                                                            : "down"
                                                                                            }`}
                                                                                    />
                                                                                ) : (
                                                                                    <i className={"fas fa-sort"} />
                                                                                ))}
                                                                        </span>
                                                                    </div>
                                                                </th>
                                                            );
                                                        }
                                                    )}
                                                </tr>
                                            </thead>
                                            <tbody >
                                                {tableData?.map((item: Row, index) => {
                                                    const hasHelpText = Object.prototype.hasOwnProperty.call(item, "helpText")
                                                    return (
                                                        <Draggable
                                                            key={index}
                                                            draggableId={`${index}`}
                                                            index={index}
                                                            isDragDisabled={true}
                                                        >
                                                            {(provided) => (
                                                                <tr
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                    className={`tw-w-full tw-h-12 tw-bg-white  tw-border tw-border-zinc-200`}
                                                                >
                                                                    <td
                                                                        className={`tw-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-white"
                                                                            : "tw-bg-gray-100"
                                                                            } tw-pl-4 tw-pr-4`}
                                                                    >
                                                                        <DragDropIcon />
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 tw-max-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-white"
                                                                            : "tw-bg-gray-100"
                                                                            } tw-cursor-pointer`}
                                                                    >
                                                                        {item?.image ? (
                                                                            <img
                                                                                src={item?.image}
                                                                                alt="image"
                                                                                className="tw-h-12 tw-w-12"
                                                                            />
                                                                        ) : (
                                                                            <ImageIcon />
                                                                        )}
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-white"
                                                                            : "tw-bg-gray-100"
                                                                            } tw-w-[30%]`}
                                                                    >
                                                                        <input
                                                                            className={`tw-placeholder-gray-200 tw-w-[95%] tw-h-9 tw-px-2.5 tw-py-2 tw-bg-gray-100 tw-rounded tw-border tw-border-zinc-300 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex `}
                                                                            value={item.title}
                                                                            disabled={true}
                                                                            placeholder="Enter Title"
                                                                        />
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-white"
                                                                            : "tw-bg-gray-100"
                                                                            } tw-w-[30%]`}
                                                                    >
                                                                        <input
                                                                            placeholder="Enter Display Value"
                                                                            className={`tw-placeholder-gray-200  tw-w-[95%] tw-h-9 tw-px-2.5 tw-py-2 tw-bg-gray-100 tw-rounded tw-border tw-border-zinc-300 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex `}
                                                                            value={item.display_value}
                                                                            disabled={true}
                                                                        />
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-white"
                                                                            : "tw-bg-gray-100"
                                                                            } tw-w-[20]`}
                                                                    >
                                                                        <input
                                                                            placeholder="Enter Math Value"
                                                                            className={`tw-placeholder-gray-200  tw-w-[95%] tw-h-9 tw-px-2.5 tw-py-2 tw-bg-gray-100  tw-rounded tw-border tw-border-zinc-300 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex `}
                                                                            type="number"
                                                                            value={item.math_value as number}
                                                                            disabled={true}
                                                                        />
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-white"
                                                                            : "tw-bg-gray-100"
                                                                            }`}
                                                                    >
                                                                        <ToggleButton
                                                                            value={item?.visibility}
                                                                            name={`toggle-${index}`}
                                                                            disabled={true}
                                                                            onChange={(e) =>
                                                                                onVisibilityChangeHandler(e, index)
                                                                            }
                                                                        />
                                                                    </td>
                                                                    <td className={`tw-h-12 ${index % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-100"}`}>
                                                                        <button
                                                                            className="tw-cursor-pointer tw-flex tw-align-middle tw-text-danger"

                                                                            disabled={true}
                                                                        >
                                                                            {hasHelpText ?
                                                                                <div className="tw-p-2.5"><InfoIcon color={'#0747A6'} /></div> :
                                                                                <AddIconWithBorder />
                                                                            }
                                                                        </button>
                                                                    </td>
                                                                    <td
                                                                        className={`tw-h-12 ${index % 2 === 0
                                                                            ? "tw-bg-white"
                                                                            : "tw-bg-gray-100"
                                                                            }`}
                                                                    ></td>
                                                                </tr>
                                                            )}
                                                        </Draggable>
                                                    );
                                                })}

                                                {provided.placeholder}
                                            </tbody>
                                        </table>
                                    </div>

                                )}
                            </Droppable>
                        </DragDropContext>
                    </div>
                </>
            )}
        </React.Fragment>
    );
};


export default ListsDetails;

